<script setup lang="ts">
import { ref, onUnmounted, watch } from "vue";
import { useWindowFocus } from "@vueuse/core";
import EdroneModal from "edrone-components/EdroneModal.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import { useTaskCompleteModal } from "./useTaskCompleteModal";
import { useRouter } from "vue-router";
import SuccessState from "./SuccessState.vue";
import DefaultState from "./DefaultState.vue";
import RewardState from "./RewardState.vue";
import { useFreemiumStore } from "vue-app/components/Freemium/Freemium.store";
import { storeToRefs } from "pinia";

const { isTaskCompleteModalOpen, closeTaskCompleteModal, options } = useTaskCompleteModal();
const focused = useWindowFocus();

const freemiumStore = useFreemiumStore();
const { paying } = storeToRefs(freemiumStore);

const router = useRouter();
const handleClose = () => {
  closeTaskCompleteModal();
  if (options.value.nextTask.callToAction) {
    router.push({
      path: options.value.nextTask.callToAction.startsWith("/")
        ? options.value.nextTask.callToAction
        : `/${options.value.nextTask.callToAction}`,
    });
    return;
  }
  router.push({ path: "/app/live-events" });
};

const stateComponents = {
  default: DefaultState,
  success: SuccessState,
  reward: RewardState,
};

const states = Object.keys(stateComponents);
const currentState = ref("default");

let timeoutId: ReturnType<typeof setTimeout> | undefined;

const stopTransition = () => {
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = undefined;
  }
};

const startTransition = () => {
  stopTransition();
  const currentIndex = states.indexOf(currentState.value);

  if (states[currentIndex] === "reward" || (states[currentIndex] === "success" && paying.value)) {
    return;
  }

  const delay = currentState.value === "default" ? 1000 : 2000;

  timeoutId = setTimeout(() => {
    const nextIndex = (currentIndex + 1) % states.length;
    currentState.value = states[nextIndex];
    startTransition();
  }, delay);
};

const confettiCanvas = ref<HTMLCanvasElement | null>(null);

interface ConfettiOptions {
  particleCount: number;
  angle: number;
  spread: number;
  origin: { x: number; y: number };
}

let myConfetti: ((opts: Partial<ConfettiOptions>) => void) | null = null;

function shotConfetti() {
  if (!focused.value) {
    return;
  }
  myConfetti?.({
    particleCount: 100,
    angle: 45,
    spread: 70,
    origin: { x: -0.2, y: 1.2 },
  });

  myConfetti?.({
    particleCount: 100,
    angle: 135,
    spread: 70,
    origin: { x: 1.2, y: 1.2 },
  });
}

const initConfetti = () => {
  if (!isTaskCompleteModalOpen.value || !focused.value) return;
  try {
    if (!confettiCanvas.value) {
      throw new Error("canvas not ready");
    }
    // @ts-ignore
    myConfetti = window.confetti.create(confettiCanvas.value, {
      resize: true,
      useWorker: true,
    });
    shotConfetti();
  } catch (e) {
    setTimeout(initConfetti, 100);
  }
};

watch(isTaskCompleteModalOpen, (isOpen) => {
  if (isOpen) {
    if (myConfetti) {
      shotConfetti();
    } else {
      initConfetti();
    }
    currentState.value = "default";
    startTransition();
  } else {
    stopTransition();
  }
});

watch(focused, (isFocused) => {
  if (isFocused && isTaskCompleteModalOpen.value) {
    shotConfetti();
  }
});

const lang = window.EDRONE_CONTEXT.user.lang || "en";

onUnmounted(() => {
  stopTransition();
});
</script>

<template>
  <EdroneModal :open="isTaskCompleteModalOpen" :show-close-icon="false" @close="closeTaskCompleteModal">
    <canvas
      ref="confettiCanvas"
      class="tw:absolute tw:z-10 tw:top-0 tw:left-0 tw:w-full tw:h-full tw:pointer-events-none"
    />
    <div class="tw:flex tw:flex-col tw:gap-5 tw:items-center tw:relative">
      <transition
        enter-active-class="tw:transition-opacity tw:duration-500 tw:ease-in-out"
        leave-active-class="tw:transition-opacity tw:duration-500 tw:ease-in-out tw:absolute tw:w-full"
        enter-from-class="tw:opacity-0"
        leave-to-class="tw:opacity-0"
      >
        <component :is="stateComponents[currentState]" :options="options" />
      </transition>
      <div class="tw:flex tw:flex-col tw:w-full tw:gap-2">
        <EdroneButton
          :disabled="!((currentState === 'success' && paying) || currentState === 'reward')"
          @click="handleClose"
        >
          {{
            options.nextTask?.callToAction && options.nextTask?.ctoDescription?.[lang]
              ? options.nextTask.ctoDescription[lang]
              : trans._liveEvents.taskCompleted.button
          }}
        </EdroneButton>
        <EdroneButton
          :disabled="!((currentState === 'success' && paying) || currentState === 'reward')"
          type="outline"
          @click="closeTaskCompleteModal"
        >
          {{ trans._liveEvents.taskCompleted.close }}
        </EdroneButton>
      </div>
    </div>
  </EdroneModal>
</template>
